import { useEffect } from "react";

const PROD_HOST = "www.trainingbasket.in";
const isNonProdHost = () => typeof window !== "undefined" && window.location.hostname !== PROD_HOST;

export function useSEO({ title, description, canonical, image, noindex = false, nofollow = false, ogTitle, ogDescription, keywords }) {
  useEffect(() => {
    const canon = normalizeCanonical(canonical);
    const absImage = image && image.startsWith("/") ? `${SITE_ORIGIN}${image}` : image;
    if (title) document.title = title;
    const set = (name, content, prop = false) => {
      if (!content) return;
      const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(prop ? "property" : "name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    set("description", description);
    set("keywords", keywords);
    set("og:title", ogTitle || title, true);
    set("og:description", ogDescription || description, true);
    set("og:type", "website", true);
    set("og:image", absImage, true);
    set("og:url", canon, true);
    set("twitter:card", "summary_large_image");
    set("twitter:title", ogTitle || title);
    set("twitter:description", ogDescription || description);
    set("twitter:image", absImage);
    if (noindex || nofollow || isNonProdHost()) {
      set("robots", isNonProdHost() ? "noindex, nofollow" : `${noindex ? "noindex" : "index"}, ${nofollow ? "nofollow" : "follow"}`);
    }
    if (canon) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canon);
    }
  }, [title, description, canonical, image, noindex, nofollow, ogTitle, ogDescription, keywords]);
}

export function JsonLd({ data }) {
  if (!data) return null;
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />;
}

export const SITE_URL = process.env.REACT_APP_BACKEND_URL || "";
export const SITE_ORIGIN = "https://www.trainingbasket.in";

const normalizeCanonical = (canonical) => {
  if (!canonical) return undefined;
  if (canonical.startsWith("/")) return `${SITE_ORIGIN}${canonical}`;
  if (typeof window !== "undefined" && canonical.startsWith(window.location.origin)) {
    return canonical.replace(window.location.origin, SITE_ORIGIN);
  }
  return canonical;
};

const CORE_PATHS = ["/", "/courses", "/blog", "/institutes"];

export function useSiteSeo(pathname) {
  useEffect(() => {
    if (!CORE_PATHS.includes(pathname)) return;
    fetch(`${API}/seo/meta?path=${encodeURIComponent(pathname)}`)
      .then((r) => r.json())
      .then((d) => {
        const seo = d.seo;
        if (!seo) return;
        const setTag = (name, content, prop = false) => {
          if (!content) return;
          const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
          let el = document.head.querySelector(sel);
          if (!el) { el = document.createElement("meta"); el.setAttribute(prop ? "property" : "name", name); document.head.appendChild(el); }
          el.setAttribute("content", content);
        };
        if (seo.meta_title) document.title = seo.meta_title;
        if (seo.meta_description) {
          const tag = document.querySelector('meta[name="description"]');
          if (tag) tag.setAttribute("content", seo.meta_description);
        }
        setTag("og:title", seo.og_title || seo.meta_title, true);
        setTag("og:description", seo.og_description || seo.meta_description, true);
        setTag("og:image", seo.og_image, true);
        setTag("keywords", seo.keywords);
        if (seo.noindex || seo.nofollow) {
          setTag("robots", `${seo.noindex ? "noindex" : "index"}, ${seo.nofollow ? "nofollow" : "follow"}`);
        }
        if (seo.canonical_url) {
          let link = document.querySelector('link[rel="canonical"]');
          if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
          link.setAttribute("href", seo.canonical_url);
        }
      })
      .catch(() => {});
  }, [pathname]);
}

import { API } from "./api";

export function useCrawlGuard(skip = false) {
  useEffect(() => {
    if (skip) return;
    if (isNonProdHost()) {
      let el = document.head.querySelector('meta[name="robots"]');
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", "robots"); document.head.appendChild(el); }
      el.setAttribute("content", "noindex, nofollow");
      return;
    }
    fetch(`${API}/seo/crawl-status`)
      .then((r) => r.json())
      .then((d) => {
        let el = document.head.querySelector('meta[name="robots"]');
        if (d.crawl_enabled) {
          if (el) el.remove();
          return;
        }
        if (!el) {
          el = document.createElement("meta");
          el.setAttribute("name", "robots");
          document.head.appendChild(el);
        }
        el.setAttribute("content", "noindex, nofollow");
      })
      .catch(() => {});
  }, [skip]);
}
